import type { CanvasNode, Position } from '../types';

/**
 * Where the lines between nodes go, in canvas units. A line leaves a node at the middle of its
 * right side and enters the next at the middle of its left side, at the connector dots just
 * outside the node.
 */

/** How far outside the node its connector dots sit. */
const CONNECTOR_GAP = 12;
/** A node without a stored size is taken to be this big. */
const DEFAULT_NODE_SIZE = { width: 300, height: 200 };
/** Lines leave and arrive level: the curve's handles reach at least this far sideways. */
const MIN_HANDLE = 50;

/** The end of a line at a node, with the node moved by `offset` (a node being dragged is drawn where it is now). */
export const connectorPoint = (
    node: Pick<CanvasNode, 'position' | 'size'> | undefined,
    side: 'input' | 'output',
    offset?: Position,
): Position => {
    if (!node) return { x: 0, y: 0 };
    const width = node.size?.width || DEFAULT_NODE_SIZE.width;
    const height = node.size?.height || DEFAULT_NODE_SIZE.height;
    return {
        x: (side === 'input' ? node.position.x - CONNECTOR_GAP : node.position.x + width + CONNECTOR_GAP) + (offset?.x ?? 0),
        y: node.position.y + height / 2 + (offset?.y ?? 0),
    };
};

const handles = (start: Position, end: Position) => {
    const reach = Math.max(Math.abs(end.x - start.x) * 0.5, MIN_HANDLE);
    return { cp1: { x: start.x + reach, y: start.y }, cp2: { x: end.x - reach, y: end.y } };
};

/** The curve from `start` to `end`, as an SVG path. */
export const connectionPath = (start: Position, end: Position): string => {
    const { cp1, cp2 } = handles(start, end);
    return `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;
};

/** The point half way along that curve, where its delete button goes. */
export const connectionMidpoint = (start: Position, end: Position): Position => {
    const { cp1, cp2 } = handles(start, end);
    // A cubic Bezier at t = 0.5: (P0 + 3 P1 + 3 P2 + P3) / 8
    return {
        x: 0.125 * start.x + 0.375 * cp1.x + 0.375 * cp2.x + 0.125 * end.x,
        y: 0.125 * start.y + 0.375 * cp1.y + 0.375 * cp2.y + 0.125 * end.y,
    };
};
